// useIntlAccaArchive — settled-slip rows for the Slip Archive. The
// `intl_acca_slips_archive_view` is a future view; until it ships we
// compute SlipArchiveRow[] from the REAL intl_acca_slips +
// intl_acca_slip_legs + intl_acca_tournaments tables, truncating
// legs_highlight to the top 3 per slip (by clv_pp DESC).
//
// DOCTRINE: never fabricate — archive is "wired but empty" until any
// slip lands in WON / LOST / VOID; the page renders that honestly.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import type {
  IntlAccaSlip,
  IntlAccaSlipLeg,
  IntlAccaTournament,
} from "../db/intl-acca-types.ts";
import type { SlipArchiveRow } from "../db/intl-acca-types-extended.ts";

export interface IntlAccaArchiveVM {
  loading: boolean;
  error: string | null;
  rows: SlipArchiveRow[];
  refetch: () => Promise<void>;
}

export function useIntlAccaArchive(): IntlAccaArchiveVM {
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [s, setS] = useState<Omit<IntlAccaArchiveVM, "refetch">>({
    loading: true, error: null, rows: [],
  });

  const fetchAll = useCallback(async () => {
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const slRes = await supabase.from("intl_acca_slips").select("*")
        .neq("status", "ACTIVE")
        .order("generated_at", { ascending: false });
      if (slRes.error) throw slRes.error;
      const slips = (slRes.data ?? []) as IntlAccaSlip[];
      if (slips.length === 0) {
        setS({ loading: false, error: null, rows: [] });
        return;
      }
      const slipIds = slips.map((x) => x.id);
      const tournIds = Array.from(new Set(slips.map((x) => x.tournament_id)));
      const [lgRes, tRes] = await Promise.all([
        supabase.from("intl_acca_slip_legs").select("*").in("slip_id", slipIds),
        supabase.from("intl_acca_tournaments").select("*").in("id", tournIds),
      ]);
      const legs = (lgRes.data ?? []) as IntlAccaSlipLeg[];
      const tourns = (tRes.data ?? []) as IntlAccaTournament[];
      const tName = new Map(tourns.map((t) => [t.id, t.name]));
      const bySlip = new Map<string, IntlAccaSlipLeg[]>();
      for (const l of legs) {
        if (!bySlip.has(l.slip_id)) bySlip.set(l.slip_id, []);
        bySlip.get(l.slip_id)!.push(l);
      }

      const rows: SlipArchiveRow[] = slips.map((sl) => {
        const legsForSlip = bySlip.get(sl.id) ?? [];
        const highlight = [...legsForSlip]
          .sort((a, b) => (b.clv_pp ?? -Infinity) - (a.clv_pp ?? -Infinity))
          .slice(0, 3)
          .map((l) => ({
            selection: l.selection,
            clv_pp: l.clv_pp,
            result: l.result,
          }));
        return {
          slip_id: sl.id,
          tournament_id: sl.tournament_id,
          tournament_name: tName.get(sl.tournament_id) ?? "—",
          slip_type: sl.slip_type,
          legs_count: legsForSlip.length,
          total_odds: sl.total_odds,
          clv_score_final: sl.clv_score_final,
          // status is non-ACTIVE here, so it is WON / LOST / VOID.
          status: sl.status as "WON" | "LOST" | "VOID",
          generated_at: sl.generated_at,
          // payout_per_100 is not a stored column — honest-null until
          // a settlement view exposes it.
          payout_per_100: null,
          legs_highlight: highlight,
        };
      });
      setS({ loading: false, error: null, rows });
    } catch (e) {
      setS({
        loading: false, error: e instanceof Error ? e.message : String(e),
        rows: [],
      });
    }
  }, [supabase]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
